import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import ApiClient, { fmtCurrency, shortId } from "../lib/api";
import { TID } from "../lib/testIds";
import { Section, KV, Tag } from "../components/Atoms";

export default function AdminPage({ config, refreshConfig }) {
  const [authed, setAuthed] = useState(!!localStorage.getItem("kk_admin_pw"));
  if (!authed) return <AdminLogin onAuthed={() => setAuthed(true)} />;
  return <AdminPanel config={config} refreshConfig={refreshConfig} onLogout={() => { localStorage.removeItem("kk_admin_pw"); setAuthed(false); }} />;
}

function AdminLogin({ onAuthed }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await ApiClient.adminLogin(pw);
      if (r.ok) {
        localStorage.setItem("kk_admin_pw", pw);
        toast.success("Admin authenticated");
        onAuthed();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="max-w-md mx-auto mt-24 px-6 fade-in">
      <Section title="Admin Access">
        <form className="space-y-3" onSubmit={submit}>
          <p className="text-[12px] text-[var(--kk-muted)] leading-relaxed">
            Enter the admin testing password. The password is set in the backend env
            <span className="kbd ml-1">ADMIN_TEST_PASSWORD</span>.
          </p>
          <input
            type="password"
            data-testid={TID.adminPasswordInput}
            className="w-full bg-[var(--kk-graphite-2)] border border-[var(--kk-line)] text-[var(--kk-ivory)] font-mono px-3 py-2.5 rounded-sm focus:outline-none focus:border-[var(--kk-gold)]"
            placeholder="••••••••"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoFocus
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-gold w-full py-2.5 text-[12px]"
            data-testid={TID.adminLoginBtn}
          >
            {busy ? "Verifying…" : "Unlock Admin"}
          </button>
        </form>
      </Section>
    </div>
  );
}

function AdminPanel({ config, refreshConfig, onLogout }) {
  const [flags, setFlags] = useState({});
  const [last, setLast] = useState(null);
  const [stress, setStress] = useState(null);
  const [cert, setCert] = useState(null);
  const [busy, setBusy] = useState(null);
  const [playerId, setPlayerId] = useState("player_demo_001");
  const [currency, setCurrency] = useState(config?.default_currency || "GBP");
  const [bet, setBet] = useState(50);
  const [delayedMs, setDelayedMs] = useState(0);

  async function refreshFlags() {
    try {
      const f = await ApiClient.adminGetFlags();
      setFlags(f);
      setDelayedMs(f.simulate_delayed_credit_ms || 0);
    } catch (e) {
      console.warn("flags load failed", e);
    }
  }
  async function refreshLast() {
    try {
      setLast(await ApiClient.adminLastTxns());
    } catch (e) {
      console.warn("last txns load failed", e);
    }
  }
  useEffect(() => {
    refreshFlags();
    refreshLast();
  }, []);

  async function setFlag(name, value) {
    try {
      const f = await ApiClient.adminSetFlag(name, value);
      setFlags(f);
    } catch (e) { toast.error(e.message); }
  }

  async function toggleMode() {
    const next = config?.mode === "mock" ? "live" : "mock";
    try {
      await ApiClient.adminSetMode(next);
      toast.success(`Mode → ${next}`);
      refreshConfig();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }

  async function runStress(rounds) {
    setBusy(`stress-${rounds}`);
    try {
      const r = await ApiClient.adminStress({
        player_id: playerId,
        currency,
        rounds,
        bet_amount: Number(bet),
        side: "RANDOM",
      });
      setStress(r);
      toast.success(`Stress ${rounds} done · ${r.wins}W / ${r.losses}L`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  }

  async function runCert() {
    setBusy("cert");
    try {
      const r = await ApiClient.certificationRun(playerId, currency, Number(bet));
      setCert(r);
      toast.success(`Certification · ${r.passed}/${r.total} passed`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  }

  async function doRollbackDebit() {
    try { const r = await ApiClient.adminRollbackDebit(); toast.success(`Rollback debit: ${r.ok ? "OK" : "FAIL"}`); refreshLast(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }
  async function doRollbackCredit() {
    try { const r = await ApiClient.adminRollbackCredit(); toast.success(`Rollback credit: ${r.ok ? "OK" : "FAIL"}`); refreshLast(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }
  async function doReplayDebit() {
    try { const r = await ApiClient.adminReplayDebit(); toast.success(`Replay debit: ${r.ok ? "OK" : "FAIL"} (${r.response?.replayed ? "idempotent" : "new"})`); refreshLast(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }
  async function doReplayCredit() {
    try { const r = await ApiClient.adminReplayCredit(); toast.success(`Replay credit: ${r.ok ? "OK" : "FAIL"} (${r.response?.replayed ? "idempotent" : "new"})`); refreshLast(); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-8 fade-in" data-testid={TID.adminPanelRoot}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--kk-gold-soft)]">
            Admin · Certification Suite
          </div>
          <h1 className="font-display text-3xl tracking-[0.18em] mt-1 text-[var(--kk-ivory)]">
            CoreBridge Test Panel
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Tag tone="gold">Mode · {config?.mode}</Tag>
          <button
            className="btn-ghost-gold px-3 py-1.5 text-[11px]"
            onClick={toggleMode}
            data-testid={TID.modeToggle}
          >
            Switch to {config?.mode === "mock" ? "Live" : "Mock"}
          </button>
          <button className="btn-ghost-gold px-3 py-1.5 text-[11px]" onClick={onLogout} data-testid={TID.adminLogoutBtn}>
            Logout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Section title="Test Context">
          <Label>Player ID</Label>
          <Input value={playerId} onChange={setPlayerId} testid="admin-player-id" />
          <Label>Currency</Label>
          <Input value={currency} onChange={setCurrency} testid="admin-currency" />
          <Label>Bet Amount</Label>
          <Input value={bet} onChange={setBet} type="number" testid="admin-bet" />
          <p className="text-[10.5px] text-[var(--kk-muted)] mt-2 leading-relaxed">
            In mock mode any player_id is auto-seeded. In live mode use a real
            CoreBridge player.
          </p>
        </Section>

        <Section title="Last Transactions">
          <KV label="Round ID" value={shortId(last?.last_round_id)} />
          <KV label="Last Debit TXN" value={shortId(last?.last_debit?.txn_id)} />
          <KV label="Last Credit TXN" value={shortId(last?.last_credit?.txn_id)} />
          <KV label="Debit Idem Key" value={shortId(last?.last_idem_debit)} />
          <KV label="Credit Idem Key" value={shortId(last?.last_idem_credit)} />
          <button className="btn-ghost-gold mt-3 px-3 py-1.5 text-[10px]" onClick={refreshLast}>
            Refresh
          </button>
        </Section>

        <Section title="Rollback / Replay">
          <div className="grid grid-cols-2 gap-2">
            <button className="btn-ghost-gold py-2 text-[11px]" onClick={doReplayDebit} data-testid={TID.replayDebitBtn}>Replay Last Debit</button>
            <button className="btn-ghost-gold py-2 text-[11px]" onClick={doReplayCredit} data-testid={TID.replayCreditBtn}>Replay Last Credit</button>
            <button className="btn-ghost-gold py-2 text-[11px]" onClick={doRollbackDebit} data-testid={TID.rollbackDebitBtn}>Rollback Last Debit</button>
            <button className="btn-ghost-gold py-2 text-[11px]" onClick={doRollbackCredit} data-testid={TID.rollbackCreditBtn}>Rollback Last Credit</button>
          </div>
          <p className="text-[10.5px] text-[var(--kk-muted)] mt-3 leading-relaxed">
            Replay calls reuse the same <span className="kbd">Idempotency-Key</span>. CoreBridge must return the original transaction and must not change the balance.
          </p>
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Section title="Simulation Flags (Mock Mode Only)">
          <FlagSwitch
            label="Provider Timeout"
            checked={!!flags.simulate_timeout}
            onChange={(v) => setFlag("simulate_timeout", v)}
            tid={TID.simTimeoutToggle}
          />
          <FlagSwitch
            label="Session Expired"
            checked={!!flags.simulate_session_expired}
            onChange={(v) => setFlag("simulate_session_expired", v)}
            tid={TID.simSessionToggle}
          />
          <FlagSwitch
            label="Insufficient Funds"
            checked={!!flags.simulate_insufficient_funds}
            onChange={(v) => setFlag("simulate_insufficient_funds", v)}
            tid={TID.simInsufficientToggle}
          />
          <FlagSwitch
            label="Network Retry (one-shot)"
            checked={!!flags.simulate_network_retry}
            onChange={(v) => setFlag("simulate_network_retry", v)}
            tid={TID.simRetryToggle}
          />
          <div className="flex items-center justify-between mt-2 gap-3">
            <Label inline>Delayed Credit (ms)</Label>
            <input
              type="number"
              data-testid={TID.simDelayedCreditInput}
              className="w-28 bg-[var(--kk-graphite-2)] border border-[var(--kk-line)] text-[var(--kk-ivory)] font-mono px-2 py-1 rounded-sm text-right text-[12px]"
              value={delayedMs}
              onChange={(e) => setDelayedMs(e.target.value)}
              onBlur={() => setFlag("simulate_delayed_credit_ms", Number(delayedMs || 0))}
            />
          </div>
          <button className="btn-ghost-gold mt-3 px-3 py-1.5 text-[11px]" data-testid={TID.simResetBtn} onClick={async () => { const f = await ApiClient.adminResetFlags(); setFlags(f); setDelayedMs(0); toast.success("Flags reset"); }}>
            Reset all flags
          </button>
        </Section>

        <Section title="Stress / Automation"
          right={<Tag tone="muted">Bet · {fmtCurrency(bet, currency)}</Tag>}
        >
          <div className="grid grid-cols-3 gap-2">
            <button className="btn-ghost-gold py-2 text-[11px]" disabled={busy === "stress-100"} onClick={() => runStress(100)} data-testid={TID.stress100Btn}>
              {busy === "stress-100" ? "Running…" : "Run 100"}
            </button>
            <button className="btn-ghost-gold py-2 text-[11px]" disabled={busy === "stress-1000"} onClick={() => runStress(1000)} data-testid={TID.stress1000Btn}>
              {busy === "stress-1000" ? "Running…" : "Run 1,000"}
            </button>
            <button className="btn-ghost-gold py-2 text-[11px]" disabled={busy === "stress-10000"} onClick={() => runStress(10000)} data-testid={TID.stress10000Btn}>
              {busy === "stress-10000" ? "Running…" : "Run 10,000"}
            </button>
          </div>
          {stress && (
            <div className="mt-4 grid grid-cols-2 gap-x-6" data-testid={TID.stressReport}>
              <KV label="Rounds Executed" value={`${stress.rounds_executed} / ${stress.rounds_requested}`} />
              <KV label="Wins" value={stress.wins} />
              <KV label="Losses" value={stress.losses} />
              <KV label="Failures" value={stress.failures} />
              <KV label="Total Debit" value={fmtCurrency(stress.total_debit, currency)} />
              <KV label="Total Credit" value={fmtCurrency(stress.total_credit, currency)} />
              <KV label="GGR" value={fmtCurrency(stress.ggr, currency)} />
              <KV label="Observed RTP" value={`${stress.observed_rtp_pct}%`} />
              <KV label="Duplicate Callbacks" value={stress.duplicate_callbacks} />
              <KV label="Reconciled" value={stress.reconciled ? "YES" : "NO"} />
              <KV label="Balance Before" value={fmtCurrency(stress.balance_before?.balance, currency)} />
              <KV label="Balance After" value={fmtCurrency(stress.balance_after?.balance, currency)} />
            </div>
          )}
        </Section>
      </div>

      <div className="mt-6">
        <Section
          title="Certification Dashboard"
          right={
            <button className="btn-gold px-4 py-2 text-[11px]" onClick={runCert} disabled={busy === "cert"} data-testid={TID.certRunBtn}>
              {busy === "cert" ? "Running…" : "Run Certification Suite"}
            </button>
          }
        >
          {!cert && (
            <p className="text-[12px] text-[var(--kk-muted)]">
              Runs 11 wallet-flow tests against CoreBridge: session, balance, debit, credit, loss, duplicate protection, rollback, insufficient funds and balance sync.
            </p>
          )}
          {cert && (
            <div data-testid={TID.certResults}>
              <div className="mb-4">
                <Tag tone={cert.passed === cert.total ? "win" : "loss"}>
                  {cert.passed} / {cert.total} passed
                </Tag>
              </div>
              <div className="space-y-3">
                {cert.results.map((r, i) => (
                  <CertCard key={i} report={r} />
                ))}
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}

function CertCard({ report }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`hairline rounded-sm`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-3">
          <span
            className={`font-mono text-[10.5px] px-2 py-0.5 rounded-sm border ${
              report.passed
                ? "text-[#aee0a8] border-[#3e6b3a] bg-[rgba(111,174,106,0.08)]"
                : "text-[#e2a09d] border-[#6b3a37] bg-[rgba(194,84,80,0.08)]"
            }`}
          >
            {report.passed ? "PASS" : "FAIL"}
          </span>
          <span className="font-display text-[13px] tracking-[0.14em] text-[var(--kk-ivory)]">{report.name}</span>
        </div>
        <span className="text-[10.5px] font-mono text-[var(--kk-muted)]">
          {report.balance_before != null && (
            <>before {Number(report.balance_before).toFixed(2)} → after {Number(report.balance_after ?? 0).toFixed(2)}</>
          )}
        </span>
      </div>
      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-[10px] text-[var(--kk-muted)] uppercase tracking-[0.2em] mb-1">Request</div>
            <pre className="code-block">{JSON.stringify(report.request, null, 2)}</pre>
          </div>
          <div>
            <div className="text-[10px] text-[var(--kk-muted)] uppercase tracking-[0.2em] mb-1">Response</div>
            <pre className="code-block">{JSON.stringify(report.response, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function FlagSwitch({ label, checked, onChange, tid }) {
  return (
    <label className="flex items-center justify-between py-2 cursor-pointer">
      <span className="text-[12px] text-[var(--kk-ivory)]">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={tid}
        onClick={() => onChange(!checked)}
        className={`relative w-10 h-5 rounded-full border transition-colors ${
          checked ? "bg-[var(--kk-gold)] border-[var(--kk-gold-deep)]" : "bg-[var(--kk-graphite-2)] border-[var(--kk-line)]"
        }`}
      >
        <span
          className={`absolute top-0.5 ${checked ? "left-5" : "left-0.5"} transition-all w-4 h-4 rounded-full bg-[var(--kk-black)]`}
        />
      </button>
    </label>
  );
}

function Label({ children, inline }) {
  return (
    <div className={`text-[10.5px] uppercase tracking-[0.22em] text-[var(--kk-muted)] ${inline ? "" : "mt-3 mb-1"}`}>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text", testid }) {
  return (
    <input
      type={type}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[var(--kk-graphite-2)] border border-[var(--kk-line)] text-[var(--kk-ivory)] font-mono px-3 py-2 rounded-sm text-[12.5px] focus:outline-none focus:border-[var(--kk-gold)]"
    />
  );
}
